// The window's top row in the Mac app (native/desktop.ts; only rendered
// when isMacShell()). The app has no title bar: macOS draws its three
// window buttons over the top-left corner (centres 28px down, the first
// one's left edge 20px in), and the dashboard's 56px top row takes the
// title bar's place, the sidebar's first row and the top bar side by side:
//
//   sidebar open   [ ● ● ●  ◧ ‹ › ……… ][ tabs / crumbs ……… ⌘K 🔔 ]
//   on the rail    [ ● ● ● ][ ◧ ‹ ›  tabs / crumbs …… ⌘K 🔔 ]
//
// With the sidebar open its first row keeps --mac-lights-inset (index.css:
// 92px, less in full screen, where macOS hides the buttons) for the
// window buttons, then the sidebar toggle, Back and Forward; the logo is
// not shown. On the rail (folded, or forced below 1024px) the rail is
// 92px wide and its first cell is empty: the window buttons sit there,
// and the three controls open the top bar instead. Below 1024px there is
// nothing to fold, so the toggle is left out, as in the browser.
//
// Both rows carry data-drag-row: their empty space drags the window, and
// the app reads where with the dragRegions message (MacShellWiring). Both
// have 1px of top padding over their 1px bottom hairline, so what is in
// them is centred at y = 28 exactly, on the window buttons' centre line
// (centred in the 55px above the hairline it would sit at 27.5).

import { useEffect, useSyncExternalStore } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLocation } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  getHistoryFlags,
  goBack,
  goForward,
  scheduleRegions,
  setSidebarToggler,
  subscribeHistoryFlags,
  watchDragRows,
} from "@/native/desktop";
import { useUi } from "@/stores/ui";

import { RAIL_QUERY, useRail } from "./SidebarNav";
import { SidebarToggle } from "./SidebarToggle";

// A click on the toggle moves it (sidebar row ⇄ top bar): focus follows it
// to its new place, as it does between the logo band and the rail.
const refocus = { current: false };

/** The sidebar toggle (unless `toggle` is false), Back and Forward. */
function WindowNavButtons({ toggle }: { toggle: boolean }) {
  const collapsed = useUi((s) => s.sidebarCollapsed);
  const toggleSidebar = useUi((s) => s.toggleSidebar);
  const { canGoBack, canGoForward } = useSyncExternalStore(subscribeHistoryFlags, getHistoryFlags);
  return (
    <>
      {toggle && (
        <SidebarToggle
          collapsed={collapsed}
          refocus={refocus}
          onToggle={() => {
            refocus.current = true;
            toggleSidebar();
          }}
        />
      )}
      <Button
        variant="ghost"
        size="icon-sm"
        className="shrink-0"
        onClick={goBack}
        disabled={!canGoBack}
        aria-label="Back"
        aria-keyshortcuts="Meta+["
        title="Back (⌘[)"
      >
        <ChevronLeft aria-hidden className="h-4 w-4" strokeWidth={1.75} />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        className="shrink-0"
        onClick={goForward}
        disabled={!canGoForward}
        aria-label="Forward"
        aria-keyshortcuts="Meta+]"
        title="Forward (⌘])"
      >
        <ChevronRight aria-hidden className="h-4 w-4" strokeWidth={1.75} />
      </Button>
    </>
  );
}

/** The sidebar's first row in the Mac app: room for the window buttons, then the controls; empty on the rail. */
export function MacSidebarTopRow() {
  const rail = useRail();
  if (rail) return <div data-drag-row="" className="h-header shrink-0 border-b border-line-subtle pt-px" />;
  return (
    <div
      data-drag-row=""
      className="flex h-header shrink-0 items-center gap-[2px] border-b border-line-subtle pl-[var(--mac-lights-inset)] pr-[12px] pt-px"
    >
      <WindowNavButtons toggle />
    </div>
  );
}

/** The top bar in the Mac app: on the rail, the controls before its own row. */
export function MacHeader({ children }: { children: React.ReactNode }) {
  const rail = useRail();
  const narrow = useMediaQuery(RAIL_QUERY);
  return (
    <header data-drag-row="" className="flex h-header shrink-0 border-b border-line-subtle pt-px">
      {rail && (
        <div className="flex shrink-0 items-center gap-[2px] pl-[8px]">
          <WindowNavButtons toggle={!narrow} />
        </div>
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </header>
  );
}

/**
 * The workspace shell's side of the protocol: keep the app told where the
 * drag rows are, and let its View menu fold the sidebar. Renders nothing.
 */
export function MacShellWiring() {
  const toggleSidebar = useUi((s) => s.toggleSidebar);
  const rail = useRail();
  const { pathname } = useLocation();
  useEffect(() => watchDragRows(), []);
  useEffect(() => {
    // Below 1024px the rail is forced: nothing to fold (as "[" there).
    setSidebarToggler(() => {
      if (!window.matchMedia(RAIL_QUERY).matches) toggleSidebar();
    });
    return () => setSidebarToggler(null);
  }, [toggleSidebar]);
  // The observers see most changes; these move the rows without resizing them.
  useEffect(() => scheduleRegions(), [rail, pathname]);
  return null;
}
